import { FC, useEffect, useState } from 'react';
import { useLocation } from 'react-use';

import checkIcon from 'assets/images/checkIcon.svg';
import formFrame from 'assets/images/form-frame.svg';

import { media } from 'atoms/breakpoints/breakpoints';
import color, { getColor } from 'atoms/colors/colors';

// Quarks
import Container from 'quarks/Container';
import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';
import Image, { ImageData } from 'quarks/Image';
import type { FlexProps } from 'quarks/interpolations/flex';
import { TextStyles } from 'quarks/styleProps/heading';

import Breadcrumbs from 'components/Breadcrumbs/Breadcrumbs';
import ComponentButton from 'components/Button/ComponentButton';
import EmbedCode from 'components/EmbedCode';
import ComponentForm from 'components/Form';
import heroRichTextParser from 'components/Hero/heroRichTextParser';
import ComponentVideo from 'components/Video/Video';

import { formatHeadingSize } from 'utils/formatHeadingSize';
import { colorTheme, toCamelCase } from 'utils/functions';
import richTextParser from 'utils/richTextParser';

import { standardPalette } from 'theme/colorPalettes';

import type { ContentfulComponentHero } from 'graphqlTypes';

interface HeroProps extends FlexProps, ContentfulComponentHero {
  backgroundIsDark: boolean;
  bottomGradient: boolean;
  alignment: string;
  displayBreadcrumbs: boolean;
  kicker: string;
}

const ComponentHero: FC<HeroProps> = ({
  alignment,
  displayBreadcrumbs,
  heading,
  headingRichText,
  headingColor,
  kicker,
  body,
  bodyColor,
  bottomGradient,
  callToAction,
  reference,
  backgroundIsDark,
  fullWidth,
  verticalCopyAlignment,
  ...props
}) => {
  const { headingSizeMobile, headingSizeTablet, headingSizeDesktop } = formatHeadingSize(props);
  const { pathname } = useLocation();
  const isFullHeightIframe = reference?.__typename === 'ContentfulComponentIframe' && reference?.isFullHeight;
  const isHomePage = pathname === '/';
  const centeredContent = alignment === 'Center';
  const leftContent = alignment === 'Left';
  const verticalAlignment =
    verticalCopyAlignment === 'Top' ? 'start' : verticalCopyAlignment === 'Bottom' ? 'end' : 'center';

  const bodyCSS = `
    word-wrap: break-word;
    ul {
      li {
        padding-left: 30px;
        &:before {
          background-image: url(${checkIcon});
          background-color: ${color.common.transparent};
          background-position: center;
          background-repeat: no-repeat;
          background-size: 100%;
          width: 20px;
          height: 20px;
          content: '';
          position: absolute;
          top: 4px;
          left: 0;
        }
      }
    }
    a, a:hover {
      color: ${getColor(standardPalette, colorTheme(bodyColor || 'Gray 700'))};
    }
  `;

  const generateBreadcrumbs = () => {
    const breadcrumbPaths = pathname?.split('/').filter(Boolean);

    const breadcrumbs = breadcrumbPaths?.map((crumb, index) => ({
      label: crumb
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' '),
      link: `/${breadcrumbPaths.slice(0, index + 1).join('/')}`,
    }));

    return breadcrumbs;
  };

  const ctaButtons =
    callToAction && callToAction.filter(item => item?.__typename === 'ContentfulComponentCallToAction');

  const [iframeHeight, setHeight] = useState(isFullHeightIframe ? 1875 : 600);

  useEffect(() => {
    const handler = (ev: any) => {
      if (ev?.data && typeof ev?.data === 'string' && ev?.data?.includes('height')) {
        const removedParam = ev?.data?.replace('paperform:', '');
        const msgData = removedParam && JSON.parse(removedParam);
        setHeight(msgData?.height || 983);
      }
    };

    window.addEventListener('message', handler);

    return () => window.removeEventListener('message', handler);
  }, []);

  const determineReference = (ref: ContentfulComponentHero['reference']) => {
    switch (ref?.__typename) {
      case 'ContentfulComponentImage':
        return (
          <Flex
            position="relative"
            order={fullWidth ? '-1' : '1'}
            bottom={0}
            right={0}
            width="100%"
            height="100%"
            md={{
              position: fullWidth ? 'absolute' : 'relative',
              width: centeredContent ? '770px' : fullWidth ? '60%' : '100%',
            }}
            lg={{ width: centeredContent ? '770px' : fullWidth ? '60%' : '100%' }}
            xl={{ width: centeredContent ? '770px' : fullWidth ? '50%' : '100%', right: fullWidth ? '5%' : '0' }}
          >
            <Image
              image={(ref?.bynderImageDesktop || ref?.imageDesktop) as ImageData}
              alt={ref?.altText || 'Hero Reference Image'}
              boxShadow={ref?.boxShadow}
              width="100%"
              objectFit="contain"
              objectPosition="center"
              roundedCorners={ref?.roundedCorners}
              display="none"
              md={{ display: 'block' }}
            />
            <Image
              image={
                (ref?.bynderImageMobile ||
                  ref?.imageMobile ||
                  ref?.bynderImageDesktop ||
                  ref?.imageDesktop) as ImageData
              }
              alt={ref?.altText || 'Hero Reference Image'}
              boxShadow={ref?.boxShadow}
              width="100%"
              objectFit="contain"
              objectPosition="center"
              roundedCorners={ref?.roundedCorners}
              md={{ display: 'none' }}
            />
          </Flex>
        );
      case 'ContentfulComponentForm':
        return (
          <Flex width="100%" maxWidth="770px" marginX="auto" position="relative">
            <ComponentForm backgroundIsDark={backgroundIsDark} {...ref} />
            <Flex
              position="absolute"
              width="80%"
              bottom="-6%"
              right="-10%"
              zIndex="-1"
              lg={{ top: '7%', right: '-20%', width: '115%' }}
            >
              <Image image={{ file: { url: formFrame } }} alt="Form Reference Frame" width="100%" height="100%" />
            </Flex>
          </Flex>
        );
      case 'ContentfulComponentIframe':
        return (
          <Container width="100%" height={`${iframeHeight}px`} maxWidth="770px" marginX="auto">
            {ref?.embedCode && <EmbedCode embedCode={ref?.embedCode} displayCode={ref?.displayCode} />}
          </Container>
        );
      case 'ContentfulComponentVideo':
        return (
          <Flex position="relative" width="100%" maxWidth="100%" lg={{ maxWidth: '992px' }}>
            <ComponentVideo {...ref} backgroundColor="None" backgroundIsDark={backgroundIsDark} />
          </Flex>
        );
      default:
        return null;
    }
  };

  const isForm = reference?.__typename === 'ContentfulComponentForm';
  const isIframe = reference?.__typename === 'ContentfulComponentIframe';

  return (
    <>
      <Flex
        className="hero-content"
        flexDirection="column"
        justifyContent={reference ? 'space-between' : `${fullWidth && !reference ? 'left' : 'center'}`}
        alignItems={verticalAlignment}
        gap="32px"
        width="100%"
        marginX="auto"
        height="fit-content"
        textColor={backgroundIsDark ? 'common-white' : 'gray-900'}
        md={{
          flexDirection: leftContent ? 'row-reverse' : centeredContent || isForm || isIframe ? 'column' : 'row',
          height: isForm ? 'max-content' : 'fit-content',
        }}
        lg={{ flexDirection: leftContent ? 'row-reverse' : centeredContent || isIframe ? 'column' : 'row' }}
        xl={{ height: reference ? `${fullWidth ? 'calc(100vh - 192px)' : 'fit-content'}` : 'fit-content' }}
        css={`
          padding-bottom: ${isForm ? '10px' : '0'};

          ${media.sm} {
            padding-top: 0;
          }

          ${media.xs} {
            padding-top: ${isHomePage ? '50px' : '0'};
          }

          .breadcrumbs {
            padding-bottom: 24px;

            ${media.sm} {
              padding-bottom: 60px;
            }
          }
        `}
        {...props}
      >
        {displayBreadcrumbs && <Breadcrumbs breadcrumbs={generateBreadcrumbs()} />}
        <Flex
          position="relative"
          zIndex="2"
          flexDirection="column"
          width="100%"
          maxWidth="100%"
          gap="24px"
          textAlign={centeredContent || isIframe ? 'center' : `${fullWidth || reference ? 'left' : 'center'}`}
          md={{ maxWidth: isHomePage ? '400px' : centeredContent || isForm ? '770px' : '470px' }}
          lg={{
            maxWidth: centeredContent
              ? '770px'
              : isIframe
              ? '100%'
              : `${isHomePage ? '400px' : reference ? '570px' : `${fullWidth ? '570px' : '100%'}`}`,
          }}
          xl={{
            maxWidth: centeredContent
              ? '770px'
              : isIframe
              ? '100%'
              : `${reference ? '570px' : `${fullWidth ? '570px' : '100%'}`}`,
          }}
        >
          {kicker && (
            <Heading
              as="h6"
              textStyle="h6"
              fontWeight="medium"
              textColor="green-300"
              css={`
                margin-bottom: -10px;
                text-transform: uppercase;
              `}
            >
              {kicker}
            </Heading>
          )}
          {(heading || headingRichText) && (
            <Heading
              as="h1"
              textStyle={(headingSizeMobile as TextStyles) || 'h3'}
              fontWeight="bold"
              textColor={colorTheme(toCamelCase(headingColor || 'Gray 900'))}
              md={{ textStyle: (headingSizeTablet as TextStyles) || 'h2', fontWeight: 'bold' }}
              lg={{ textStyle: (headingSizeDesktop as TextStyles) || 'h1', fontWeight: 'bold' }}
            >
              {heading || heroRichTextParser(headingRichText)}
            </Heading>
          )}
          {body && (
            <Flex flexDirection="column" gap="24px" textColor={colorTheme(bodyColor || 'Gray 700')} css={bodyCSS}>
              {body && richTextParser(body)}
            </Flex>
          )}
          {ctaButtons && ctaButtons.length > 0 && (
            <Flex
              flexDirection="column"
              width="100%"
              gap="16px"
              alignItems={centeredContent ? 'center' : undefined}
              justifyContent={
                centeredContent ? 'center' : reference ? undefined : `${fullWidth && !reference ? 'left' : 'center'}`
              }
              sm={{ flexDirection: 'row' }}
              css={`
                @media (max-width: 575px) {
                  align-items: center;
                }
              `}
            >
              {ctaButtons.map(
                button =>
                  button?.__typename === 'ContentfulComponentCallToAction' && (
                    <ComponentButton key={button?.contentful_id} {...button} />
                  ),
              )}
            </Flex>
          )}
        </Flex>
        {reference && determineReference(reference)}
      </Flex>
      {fullWidth && bottomGradient !== false && (
        <Flex
          position="absolute"
          width="100%"
          height="85%"
          bottom={0}
          left={0}
          css={{
            background: `${
              isHomePage
                ? 'linear-gradient(183.09deg,rgba(17,12,57,0) 41.42%,#110C39 88.47%)'
                : 'linear-gradient(183.09deg, rgba(17, 12, 57, 0) 8.42%, #110C39 44.47%)'
            }`,
            [media.sm]: {
              background: 'linear-gradient(183.09deg, rgba(17, 12, 57, 0) 8.42%, #110C39 44.47%)',
            },
            [media.md]: {
              background: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 8.47%, #110C39 97.39%)',
            },
          }}
          sm={{ height: '48%' }}
          md={{ height: '170px' }}
        />
      )}
    </>
  );
};

export default ComponentHero;
